import { ApiKeyCreated, ApiKeyInfo, OptionsApi } from '@core/swagger-client';
import { Button, Delete, ErrorMessagePage, Heading, Input, LoaderPage } from '@core/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

type CreateKeyForm = { name: string };

export const ApiKeys = () => {
    const { t } = useTranslation();
    const [createdKey, setCreatedKey] = useState<ApiKeyCreated | null>(null);

    const {
        register,
        formState: { errors },
        handleSubmit,
        resetField
    } = useForm<CreateKeyForm>({
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
        defaultValues: { name: '' }
    });

    const { data: apiKeys, error, isLoading, refetch } = useQuery(OptionsApi.apiKeys.getAll());

    const createMutation = useMutation(OptionsApi.apiKeys.create());
    const onSubmit: SubmitHandler<CreateKeyForm> = (data) => {
        createMutation.mutate(data.name, {
            onSuccess: (result) => {
                setCreatedKey(result);
                resetField('name');
                refetch();
            }
        });
    };

    const revokeMutation = useMutation(OptionsApi.apiKeys.revoke());
    const revokeKey = (keyId: string) => {
        revokeMutation.mutate(keyId, { onSuccess: () => refetch() });
    };

    if (error) {
        return <ErrorMessagePage apiError={error} />;
    }

    if (isLoading) {
        return <LoaderPage />;
    }

    const activeKeys = (apiKeys ?? []).filter((k: ApiKeyInfo) => !k.isRevoked);

    return (
        <div className="p-4 md:p-8">
            <Heading variant="h2" classNames="pb-4 md:pb-10">
                {t('Account.ApiKeys.PageHeading')}
            </Heading>
            <div className="mb-10">
                <Trans i18nKey="Account.ApiKeys.Description" />
            </div>

            {createdKey && (
                <div className="mb-10 p-4 border border-success rounded-button bg-success/10">
                    <Heading variant="h3" classNames="pb-2">
                        {t('Account.ApiKeys.Created.Title')}
                    </Heading>
                    <p className="mb-2 text-sm font-semibold text-warning">
                        {t('Account.ApiKeys.Created.Warning')}
                    </p>
                    <code className="block break-all bg-white border border-gray-200 rounded p-3 text-sm select-all">
                        {createdKey.plaintextKey}
                    </code>
                    <Button classNames="mt-4" variant="secondary" onClick={() => setCreatedKey(null)}>
                        {t('Account.ApiKeys.Button.Dismiss')}
                    </Button>
                </div>
            )}

            <form className="max-w-lg mb-10" onSubmit={handleSubmit(onSubmit)}>
                <Input
                    label={t('Account.ApiKeys.Label.Name')}
                    {...register('name', { required: t('Account.ApiKeys.Validation.NameRequired') })}
                    errorMessage={errors.name?.message}
                />
                <Button isLoading={createMutation.isPending} variant="primary">
                    {t('Account.ApiKeys.Button.Create')}
                </Button>
            </form>

            {activeKeys.length > 0 && (
                <>
                    <Heading variant="h3" classNames="pb-4 md:pb-6">
                        {t('Account.ApiKeys.Heading.ActiveKeys')}
                    </Heading>
                    <div className="flex flex-col gap-4">
                        {activeKeys.map((key: ApiKeyInfo) => (
                            <div className="flex items-center border border-gray-200 rounded-button overflow-hidden" key={key.keyId}>
                                <div className="flex-1 ms-4 me-3 py-3">
                                    <span className="block font-medium">{key.name}</span>
                                    <span className="block text-sm text-gray-500">
                                        <Trans
                                            i18nKey="Account.ApiKeys.CreatedAt"
                                            values={{ date: new Date(key.createdAt).toLocaleDateString() }}
                                        />
                                    </span>
                                </div>
                                <Button
                                    classNames="flex-none"
                                    iconbtn
                                    onClick={() => revokeKey(key.keyId)}
                                    isLoading={revokeMutation.isPending}
                                >
                                    <Delete classNames="size-6 flex-none stroke-warning" />
                                    <span className="sr-only">
                                        <Trans i18nKey="Account.ApiKeys.Button.Revoke" values={{ name: key.name }} />
                                    </span>
                                </Button>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};
